(() => {
  const splitLines = (value) => String(value || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
  const normalizeAngle = (value) => ((value % 360) + 360) % 360;
  const signedAngle = (value) => {
    const normalized = normalizeAngle(value);
    return normalized > 180 ? normalized - 360 : normalized;
  };
  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character]);

  const setup = (root) => {
    if (root.dataset.wheelInitialized === 'true') return;

    const readJson = (selector) => {
      const element = root.querySelector(selector);
      if (!element) return [];
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        return [];
      }
    };

    const festivals = readJson('[data-wheel-festivals-json]').sort((a, b) => Number(a.sequence) - Number(b.sequence));
    const routes = readJson('[data-wheel-routes-json]').sort((a, b) => Number(a.order) - Number(b.order));
    const routeSteps = readJson('[data-wheel-route-steps-json]');
    const labels = readJson('[data-wheel-labels-json]');
    if (!festivals.length) return;

    const state = {
      hemisphere: 'north',
      mode: 'explore',
      layer: 'season',
      selected: null,
      currentHandle: null,
      nextHandle: null,
      route: null,
      routeIndex: 0,
      rotation: 0,
      dragging: false,
      pointerMoved: false,
      gestureStartedOnNode: false,
      pointerFestivalHandle: null,
      pointerStartAngle: 0,
      rotationStart: 0,
      sheetStartY: 0,
      sheetStartOffset: 0,
      sheetDragged: false,
      tapTimer: null,
      suppressNodeClick: false
    };

    const disc = root.querySelector('[data-wheel-disc]');
    const canvas = root.querySelector('[data-wheel-canvas]');
    const detail = root.querySelector('[data-wheel-detail]');
    const stage = root.querySelector('.wheel-year__stage');
    const stageToolbar = root.querySelector('.wheel-year__stage-toolbar');
    const centerDate = root.querySelector('[data-wheel-center-date]');
    const centerSymbol = root.querySelector('[data-wheel-center-symbol]');
    const centerTitle = root.querySelector('[data-wheel-center-title]');
    const centerSeason = root.querySelector('[data-wheel-center-season]');
    const detailKicker = root.querySelector('[data-wheel-detail-kicker]');
    const detailTitle = root.querySelector('[data-wheel-detail-title]');
    const detailSymbol = root.querySelector('[data-wheel-detail-symbol]');
    const detailDate = root.querySelector('[data-wheel-detail-date]');
    const detailEvidence = root.querySelector('[data-wheel-detail-evidence]');
    const detailCopy = root.querySelector('[data-wheel-detail-copy]');
    const detailChips = root.querySelector('[data-wheel-detail-chips]');
    const detailGuide = root.querySelector('[data-wheel-detail-guide]');
    const detailSource = root.querySelector('[data-wheel-detail-source]');
    const explorer = root.querySelector('[data-wheel-explorer]');
    const routePlayer = root.querySelector('[data-wheel-route-player]');
    const routeProgress = root.querySelector('[data-wheel-route-progress]');
    const routeCount = root.querySelector('[data-wheel-route-count]');
    const routeHeading = root.querySelector('[data-wheel-route-heading]');
    const routeNarrative = root.querySelector('[data-wheel-route-narrative]');
    const routePrevious = root.querySelector('[data-wheel-route-prev]');
    const routeNext = root.querySelector('[data-wheel-route-next]');

    const festivalByHandle = new Map(festivals.map((festival) => [festival.handle, festival]));
    const nodeByHandle = new Map(
      [...root.querySelectorAll('.wheel-year__festival-node[data-wheel-festival]')]
        .map((node) => [node.dataset.wheelFestival, node])
    );
    const northAnchors = {
      imbolc: [1, 1],
      ostara: [2, 20],
      beltane: [4, 1],
      litha: [5, 21],
      lughnasadh: [7, 1],
      mabon: [8, 22],
      samhain: [9, 31],
      yule: [11, 21]
    };
    const southAnchors = {
      lughnasadh: [1, 1],
      mabon: [2, 20],
      samhain: [3, 30],
      yule: [5, 21],
      imbolc: [7, 1],
      ostara: [8, 22],
      beltane: [9, 31],
      litha: [11, 21]
    };

    const getFestival = (handle) => festivalByHandle.get(handle) || festivals[0];
    const getDateText = (festival) => state.hemisphere === 'north' ? festival.northDate : festival.southDate;
    const getSeasonText = (festival) => state.hemisphere === 'north' ? festival.northSeason : festival.southSeason;
    const trackInteraction = (action, details = {}) => {
      const event = {
        event: 'wheel_of_year_interaction',
        wheel_action: action,
        wheel_mode: state.mode,
        wheel_hemisphere: state.hemisphere,
        wheel_layer: state.layer,
        wheel_festival: state.selected || '',
        wheel_route: state.route || '',
        ...details
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
      document.dispatchEvent(new CustomEvent('wheel:interaction', { detail: event }));
    };
    const syncUrl = (values = {}) => {
      if (!window.history?.replaceState) return;
      const url = new URL(window.location.href);
      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') url.searchParams.delete(key);
        else url.searchParams.set(key, String(value));
      });
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    };
    const formatToday = () => new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      month: 'long',
      day: 'numeric'
    }).format(new Date());

    const calculateSeasonPosition = () => {
      const now = new Date();
      const anchors = state.hemisphere === 'north' ? northAnchors : southAnchors;
      const year = now.getFullYear();
      const timeline = Object.entries(anchors).map(([handle, parts]) => ({
        handle,
        date: new Date(year, parts[0], parts[1], 12)
      })).sort((a, b) => a.date - b.date);
      const previous = [...timeline].reverse().find((item) => item.date <= now) || {
        handle: timeline[timeline.length - 1].handle,
        date: new Date(year - 1, anchors[timeline[timeline.length - 1].handle][0], anchors[timeline[timeline.length - 1].handle][1], 12)
      };
      const next = timeline.find((item) => item.date > now) || {
        handle: timeline[0].handle,
        date: new Date(year + 1, anchors[timeline[0].handle][0], anchors[timeline[0].handle][1], 12)
      };
      state.currentHandle = previous.handle;
      state.nextHandle = next.handle;
    };

    const setRotation = (rotation, animate = true) => {
      const nextRotation = animate
        ? state.rotation + signedAngle(rotation - state.rotation)
        : rotation;
      state.rotation = nextRotation;
      if (!animate) disc.style.transition = 'none';
      disc.style.setProperty('--wheel-rotation', `${nextRotation}deg`);
      if (!animate) requestAnimationFrame(() => disc.style.removeProperty('transition'));
    };

    const alignStageToolbar = () => {
      if (!stage || !stageToolbar || window.matchMedia('(max-width: 1100px)').matches) {
        stageToolbar?.style.removeProperty('--wheel-toolbar-center');
        return;
      }
      const stageRect = stage.getBoundingClientRect();
      const discRect = disc.getBoundingClientRect();
      const center = discRect.top - stageRect.top + discRect.height / 2;
      stageToolbar.style.setProperty('--wheel-toolbar-center', `${center}px`);
    };

    if ('ResizeObserver' in window) {
      const toolbarObserver = new ResizeObserver(alignStageToolbar);
      toolbarObserver.observe(stage);
      toolbarObserver.observe(disc);
    }
    window.addEventListener('resize', alignStageToolbar, { passive: true });
    requestAnimationFrame(alignStageToolbar);

    if ('IntersectionObserver' in window && stage) {
      const stageObserver = new IntersectionObserver(([entry]) => {
        const stageVisible = entry.isIntersecting;
        root.classList.toggle('is-wheel-stage-visible', stageVisible);
        if (!stageVisible) {
          detail.classList.remove('is-open', 'is-peek', 'is-dragging');
          detail.style.removeProperty('transform');
        }
      }, {
        threshold: 0,
        rootMargin: '0px 0px -8% 0px'
      });
      stageObserver.observe(stage);
    } else {
      root.classList.add('is-wheel-stage-visible');
    }

    const layerContent = (festival) => {
      const date = getDateText(festival);
      const season = getSeasonText(festival);
      const relatedProducts = Array.isArray(festival.relatedProducts) ? festival.relatedProducts : [];
      const relatedProductsMarkup = relatedProducts.length
        ? `<div class="wheel-year__detail-products">
            <h3>${escapeHtml(labels.relatedProducts)}</h3>
            ${relatedProducts.map((product) => `
              <a href="${escapeHtml(product.url)}" data-wheel-product="${escapeHtml(product.handle)}">
                ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.alt || product.title)}" loading="lazy" width="160" height="160">` : ''}
                <span><strong>${escapeHtml(product.title)}</strong>${product.price ? `<small>${escapeHtml(product.price)}</small>` : ''}</span>
              </a>
            `).join('')}
          </div>`
        : '';
      const values = {
        season: {
          title: labels.season,
          body: `<p>${escapeHtml(festival.shortAnswer)}</p><h3>${escapeHtml(season)}</h3><p>${escapeHtml(date)} · ${escapeHtml(festival.festivalType)}</p>`,
          chips: [season, festival.festivalType]
        },
        history: {
          title: labels.history,
          body: `<p>${escapeHtml(festival.history)}</p>`,
          chips: [festival.evidence]
        },
        symbols: {
          title: labels.symbols,
          body: `<p>${escapeHtml(festival.meaning).replace(/\n/g, '<br>')}</p>${relatedProductsMarkup}`,
          chips: [...splitLines(festival.symbols), ...splitLines(festival.relatedSymbols), ...splitLines(festival.colors)]
        },
        deities: {
          title: labels.deities,
          body: `<p>${splitLines(festival.deities).length ? splitLines(festival.deities).map((name) => escapeHtml(name)).join('<br>') : escapeHtml(festival.shortAnswer)}</p>`,
          chips: splitLines(festival.deities)
        },
        practices: {
          title: labels.practices,
          body: `<p>${escapeHtml(festival.modernPractice)}</p><h3>Foods & plants</h3><p>${escapeHtml(festival.foodsPlants).replace(/\n/g, '<br>')}</p>`,
          chips: splitLines(festival.foodsPlants)
        },
        articles: {
          title: labels.articles,
          body: Array.isArray(festival.articles) && festival.articles.length
            ? `<div class="wheel-year__article-links">${festival.articles.map((article) => `
                <a href="${escapeHtml(article.url)}">
                  <strong>${escapeHtml(article.title)}</strong>
                  ${article.summary ? `<span>${escapeHtml(article.summary)}</span>` : ''}
                </a>
              `).join('')}</div>`
            : `<p>${escapeHtml(labels.noArticles || festival.shortAnswer)}</p>`,
          chips: []
        }
      };
      return values[state.layer] || values.season;
    };

    const updateDetail = (festival, openSheet = false) => {
      const content = layerContent(festival);
      detailKicker.textContent = content.title;
      detailTitle.textContent = festival.name;
      detailSymbol.textContent = festival.symbol;
      detailDate.textContent = `${getDateText(festival)} · ${getSeasonText(festival)}`;
      detailEvidence.textContent = festival.evidence;
      detailCopy.innerHTML = content.body;
      detailChips.innerHTML = content.chips.map((chip) => `<span>${escapeHtml(chip)}</span>`).join('');
      detailGuide.href = festival.guideUrl || '/blogs/pagan-festivals';
      detailGuide.hidden = !festival.guideUrl;
      detailSource.href = festival.sourceUrl || '#';
      detailSource.textContent = festival.sourceLabel || detailSource.textContent;
      detailSource.hidden = !festival.sourceUrl;
      if (openSheet && matchMedia('(max-width: 740px)').matches) {
        detail.classList.add('is-peek');
      }
    };

    const updateCenterForSelection = (festival) => {
      if (centerDate) centerDate.textContent = labels.selected;
      if (centerSymbol) centerSymbol.textContent = festival.symbol;
      if (centerTitle) centerTitle.textContent = festival.name;
      if (centerSeason) centerSeason.textContent = `${getDateText(festival)} · ${getSeasonText(festival)}`;
    };

    const updateCenterForToday = () => {
      const previous = getFestival(state.currentHandle);
      const next = getFestival(state.nextHandle);
      if (centerDate) centerDate.textContent = `${labels.today} · ${formatToday()}`;
      if (centerSymbol) centerSymbol.textContent = next.symbol;
      if (centerTitle) centerTitle.textContent = `${labels.between} ${previous.name} & ${next.name}`;
      if (centerSeason) centerSeason.textContent = `${getSeasonText(next)} · ${next.name} ${getDateText(next)}`;
    };

    const selectFestival = (handle, options = {}) => {
      const festival = getFestival(handle);
      state.selected = handle;
      nodeByHandle.forEach((node, nodeHandle) => {
        const active = nodeHandle === handle;
        node.classList.remove('is-current-target');
        node.removeAttribute('aria-current');
        node.classList.toggle('is-active', active);
        node.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      setRotation(-Number(festival.angle || 0), options.animate !== false);
      updateCenterForSelection(festival);
      updateDetail(festival, options.openSheet !== false);
      syncUrl({ festival: handle });
    };

    const selectFestivalWithTapFeedback = (handle) => {
      const node = nodeByHandle.get(handle);
      const isMobileTap = window.matchMedia('(max-width: 740px) and (hover: none)').matches;
      if (!isMobileTap || !node) {
        selectFestival(handle);
        return;
      }

      if (state.tapTimer) window.clearTimeout(state.tapTimer);
      nodeByHandle.forEach((festivalNode) => {
        festivalNode.classList.remove('is-active', 'is-tap-feedback');
        festivalNode.setAttribute('aria-pressed', 'false');
      });
      node.classList.add('is-tap-feedback');
      node.blur();

      state.tapTimer = window.setTimeout(() => {
        state.tapTimer = null;
        node.classList.remove('is-tap-feedback');
        selectFestival(handle);
      }, 480);
    };

    const returnToCurrent = () => {
      state.selected = null;
      const next = getFestival(state.nextHandle);
      nodeByHandle.forEach((node, handle) => {
        const currentTarget = handle === next.handle;
        node.classList.remove('is-active');
        node.setAttribute('aria-pressed', 'false');
        node.classList.toggle('is-current-target', currentTarget);
        if (currentTarget) node.setAttribute('aria-current', 'date');
        else node.removeAttribute('aria-current');
      });
      setRotation(-Number(next.angle || 0));
      updateCenterForToday();
      updateDetail(next);
      syncUrl({ festival: null, route: null, route_step: null });
    };

    const updateHemisphere = (hemisphere) => {
      state.hemisphere = hemisphere;
      root.querySelectorAll('[data-wheel-hemisphere]').forEach((button) => {
        const active = button.dataset.wheelHemisphere === hemisphere;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      nodeByHandle.forEach((node, handle) => {
        const date = node.querySelector('[data-wheel-node-date]');
        if (date) date.textContent = getDateText(getFestival(handle));
      });
      root.querySelectorAll('[data-wheel-knowledge-date]').forEach((element) => {
        element.textContent = element.dataset[hemisphere] || '';
      });
      root.querySelectorAll('[data-wheel-knowledge-season]').forEach((element) => {
        element.textContent = element.dataset[hemisphere] || '';
      });
      calculateSeasonPosition();
      if (state.selected) selectFestival(state.selected, { openSheet: false });
      else returnToCurrent();
      syncUrl({ hemisphere: hemisphere === 'south' ? 'south' : null });
    };

    const updateMode = (mode) => {
      state.mode = mode;
      root.querySelectorAll('[data-wheel-mode]').forEach((button) => {
        const active = button.dataset.wheelMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      root.querySelectorAll('[data-wheel-view]').forEach((view) => {
        view.hidden = view.dataset.wheelView !== mode;
      });
      syncUrl({ wheel_mode: mode === 'knowledge' ? 'knowledge' : null });
    };

    const updateLayer = (layer) => {
      state.layer = layer;
      root.querySelectorAll('[data-wheel-layer]').forEach((button) => {
        const active = button.dataset.wheelLayer === layer;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      updateDetail(getFestival(state.selected || state.nextHandle));
      syncUrl({ wheel_layer: layer === 'season' ? null : layer });
    };

    root.querySelectorAll('[data-wheel-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        updateMode(button.dataset.wheelMode);
        trackInteraction('mode_change');
      });
    });

    root.querySelectorAll('[data-wheel-hemisphere]').forEach((button) => {
      button.addEventListener('click', () => {
        updateHemisphere(button.dataset.wheelHemisphere);
        trackInteraction('hemisphere_change');
      });
    });

    root.querySelectorAll('[data-wheel-layer]').forEach((button) => {
      button.addEventListener('click', () => {
        updateLayer(button.dataset.wheelLayer);
        trackInteraction('layer_change');
      });
    });

    nodeByHandle.forEach((node) => {
      node.setAttribute('aria-pressed', 'false');
    });

    canvas.addEventListener('click', (event) => {
      const node = event.target.closest('.wheel-year__festival-node[data-wheel-festival]');
      if (!node || state.suppressNodeClick) return;
      const handle = node.dataset.wheelFestival;
      selectFestivalWithTapFeedback(handle);
      trackInteraction('festival_select', { wheel_festival: handle });
    }, { capture: true });

    root.querySelector('[data-wheel-return-current]')?.addEventListener('click', returnToCurrent);

    const angleFromPointer = (event) => {
      const rect = disc.getBoundingClientRect();
      const x = event.clientX - (rect.left + rect.width / 2);
      const y = event.clientY - (rect.top + rect.height / 2);
      return Math.atan2(y, x) * 180 / Math.PI + 90;
    };

    const festivalFromPointer = (event) => {
      const pointerAngle = angleFromPointer(event);
      return festivals.reduce((best, festival) => {
        const nodeAngle = Number(festival.angle || 0) + state.rotation;
        const distance = Math.abs(signedAngle(nodeAngle - pointerAngle));
        return !best || distance < best.distance ? { festival, distance } : best;
      }, null)?.festival;
    };

    canvas.addEventListener('pointerdown', (event) => {
      const isMobileGesture = window.matchMedia('(max-width: 740px)').matches;
      const festivalNode = event.target.closest('.wheel-year__festival-node[data-wheel-festival]');
      if (event.target.closest('a, [data-wheel-scroll-zone]')) return;
      if (event.target.closest('button') && !festivalNode) return;
      if (
        isMobileGesture
        && !event.target.closest('[data-wheel-disc]')
      ) return;
      if (state.tapTimer) {
        window.clearTimeout(state.tapTimer);
        state.tapTimer = null;
        nodeByHandle.forEach((node) => node.classList.remove('is-tap-feedback'));
      }
      state.dragging = true;
      state.pointerMoved = false;
      state.gestureStartedOnNode = Boolean(festivalNode);
      state.pointerFestivalHandle = festivalFromPointer(event)?.handle || null;
      state.suppressNodeClick = false;
      state.pointerStartAngle = angleFromPointer(event);
      state.rotationStart = state.rotation;
      canvas.setPointerCapture(event.pointerId);
    }, { capture: true });

    canvas.addEventListener('pointermove', (event) => {
      if (!state.dragging) return;
      const delta = signedAngle(angleFromPointer(event) - state.pointerStartAngle);
      if (Math.abs(delta) > 3) state.pointerMoved = true;
      setRotation(state.rotationStart + delta, false);
    });

    const finishDrag = (event) => {
      if (!state.dragging) return;
      state.dragging = false;
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      const nearest = festivals.reduce((best, festival) => {
        const distance = Math.abs(signedAngle(Number(festival.angle) + state.rotation));
        return !best || distance < best.distance ? { festival, distance } : best;
      }, null);
      if (nearest) {
        if (
          !state.pointerMoved
          && state.pointerFestivalHandle
        ) {
          if (window.matchMedia('(max-width: 740px) and (hover: none)').matches) {
            selectFestivalWithTapFeedback(state.pointerFestivalHandle);
          } else {
            selectFestival(state.pointerFestivalHandle);
          }
          trackInteraction('festival_select', { wheel_festival: state.pointerFestivalHandle });
        } else {
          selectFestival(nearest.festival.handle);
        }
      }
      state.pointerFestivalHandle = null;
      if (state.gestureStartedOnNode) {
        state.suppressNodeClick = true;
        window.setTimeout(() => {
          state.suppressNodeClick = false;
        }, 0);
      }
    };

    canvas.addEventListener('pointerup', finishDrag);
    canvas.addEventListener('pointercancel', finishDrag);

    const stepsForRoute = (handle) => routeSteps
      .filter((step) => step.routeHandle === handle)
      .sort((a, b) => Number(a.stepNumber) - Number(b.stepNumber));

    const renderRouteStep = () => {
      if (!state.route) return;
      const route = routes.find((item) => item.handle === state.route);
      const steps = stepsForRoute(state.route);
      const step = steps[state.routeIndex];
      if (!route || !step) return;
      selectFestival(step.festivalHandle, { openSheet: false });
      nodeByHandle.forEach((node, handle) => {
        node.classList.toggle('is-route', steps.some((item) => item.festivalHandle === handle));
      });
      routePlayer.hidden = false;
      routeCount.textContent = `${route.name} · ${labels.step} ${state.routeIndex + 1} ${labels.of} ${steps.length}`;
      routeHeading.textContent = step.heading;
      routeNarrative.textContent = step.narrative;
      routeProgress.style.width = `${((state.routeIndex + 1) / steps.length) * 100}%`;
      routePrevious.disabled = state.routeIndex === 0;
      routeNext.textContent = state.routeIndex === steps.length - 1 ? labels.finish : `${labels.next} →`;
      syncUrl({
        route: state.route,
        route_step: state.routeIndex + 1,
        festival: step.festivalHandle
      });
      if (matchMedia('(max-width: 740px)').matches) {
        detail.classList.remove('is-open', 'is-peek');
      }
    };

    const startRoute = (handle) => {
      state.route = handle;
      state.routeIndex = 0;
      renderRouteStep();
      explorer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      trackInteraction('route_start', { wheel_route: handle, wheel_route_step: 1 });
    };

    const exitRoute = () => {
      const completedRoute = state.route;
      state.route = null;
      state.routeIndex = 0;
      routePlayer.hidden = true;
      nodeByHandle.forEach((node) => node.classList.remove('is-route'));
      returnToCurrent();
      trackInteraction('route_exit', { wheel_route: completedRoute || '' });
    };

    root.querySelectorAll('[data-wheel-route]').forEach((button) => {
      button.addEventListener('click', () => startRoute(button.dataset.wheelRoute));
    });

    routePrevious?.addEventListener('click', () => {
      if (state.routeIndex > 0) {
        state.routeIndex -= 1;
        renderRouteStep();
        trackInteraction('route_step', { wheel_route_step: state.routeIndex + 1 });
      }
    });

    routeNext?.addEventListener('click', () => {
      const steps = stepsForRoute(state.route);
      if (state.routeIndex >= steps.length - 1) exitRoute();
      else {
        state.routeIndex += 1;
        renderRouteStep();
        trackInteraction('route_step', { wheel_route_step: state.routeIndex + 1 });
      }
    });

    root.querySelector('[data-wheel-route-exit]')?.addEventListener('click', exitRoute);

    detail.addEventListener('click', (event) => {
      const festivalButton = event.target.closest('[data-wheel-mobile-festival]');
      const routeButton = event.target.closest('[data-wheel-mobile-route]');
      if (festivalButton) {
        detailGuide.hidden = false;
        selectFestival(festivalButton.dataset.wheelMobileFestival);
        detail.classList.add('is-peek');
        trackInteraction('festival_select', { wheel_festival: festivalButton.dataset.wheelMobileFestival });
      }
      if (routeButton) {
        detailGuide.hidden = false;
        startRoute(routeButton.dataset.wheelMobileRoute);
        detail.classList.remove('is-open', 'is-peek');
      }
      const productLink = event.target.closest('[data-wheel-product]');
      if (productLink) {
        trackInteraction('product_click', { wheel_product: productLink.dataset.wheelProduct });
      }
    });

    root.addEventListener('click', (event) => {
      const guideLink = event.target.closest('[data-wheel-detail-guide]');
      const knowledgeProduct = event.target.closest('[data-wheel-knowledge-product]');
      if (guideLink) trackInteraction('guide_click');
      if (knowledgeProduct) {
        trackInteraction('product_click', {
          wheel_product: knowledgeProduct.dataset.wheelKnowledgeProduct,
          wheel_festival: knowledgeProduct.dataset.wheelFestival || ''
        });
      }
    });

    const sheetHandle = root.querySelector('[data-wheel-sheet-handle]');
    sheetHandle?.addEventListener('click', () => {
      if (state.sheetDragged) {
        state.sheetDragged = false;
        return;
      }
      if (detail.classList.contains('is-open')) {
        detail.classList.remove('is-open');
        detail.classList.add('is-peek');
      } else if (detail.classList.contains('is-peek')) {
        detail.classList.remove('is-peek');
      } else {
        detail.classList.add('is-open');
      }
    });

    sheetHandle?.addEventListener('pointerdown', (event) => {
      state.sheetDragged = false;
      state.sheetStartY = event.clientY;
      const transform = getComputedStyle(detail).transform;
      state.sheetStartOffset = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m42;
      detail.classList.add('is-dragging');
      sheetHandle.setPointerCapture(event.pointerId);
    });

    sheetHandle?.addEventListener('pointermove', (event) => {
      if (!sheetHandle.hasPointerCapture(event.pointerId)) return;
      const delta = event.clientY - state.sheetStartY;
      if (Math.abs(delta) > 6) state.sheetDragged = true;
      const offset = Math.max(0, state.sheetStartOffset + delta);
      detail.style.transform = `translateY(${offset}px)`;
    });

    const finishSheetDrag = (event) => {
      if (!sheetHandle?.hasPointerCapture(event.pointerId)) return;
      sheetHandle.releasePointerCapture(event.pointerId);
      const delta = event.clientY - state.sheetStartY;
      if (delta > 90) {
        detail.classList.remove('is-open');
        if (delta < 220) detail.classList.add('is-peek');
        else detail.classList.remove('is-peek');
      } else if (delta < -70) {
        detail.classList.remove('is-peek');
        detail.classList.add('is-open');
      }
      detail.classList.remove('is-dragging');
      requestAnimationFrame(() => detail.style.removeProperty('transform'));
    };

    sheetHandle?.addEventListener('pointerup', finishSheetDrag);
    sheetHandle?.addEventListener('pointercancel', finishSheetDrag);

    calculateSeasonPosition();
    const initialParams = new URL(window.location.href).searchParams;
    const initialHemisphere = initialParams.get('hemisphere') === 'south' ? 'south' : 'north';
    const initialMode = initialParams.get('wheel_mode') === 'knowledge' ? 'knowledge' : 'explore';
    const initialLayer = ['season', 'history', 'symbols', 'deities', 'practices', 'articles'].includes(initialParams.get('wheel_layer'))
      ? initialParams.get('wheel_layer')
      : 'season';
    const initialFestival = initialParams.get('festival');
    const initialRoute = initialParams.get('route');
    updateHemisphere(initialHemisphere);
    updateMode(initialMode);
    updateLayer(initialLayer);
    if (initialRoute && routes.some((route) => route.handle === initialRoute)) {
      state.route = initialRoute;
      const routeStepsFromUrl = stepsForRoute(initialRoute);
      const requestedStep = Math.max(1, Number(initialParams.get('route_step')) || 1);
      state.routeIndex = Math.min(requestedStep, routeStepsFromUrl.length) - 1;
      renderRouteStep();
    } else if (initialFestival && festivalByHandle.has(initialFestival)) {
      selectFestival(initialFestival, { openSheet: false, animate: false });
    }
    root.dataset.wheelInitialized = 'true';
  };

  const initialize = () => document.querySelectorAll('[data-wheel-year]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
